<template>
  <!-- A tab beside the grid on a wide screen, a sheet over the keyboard
       everywhere else - the same two shapes as ClueList. -->
  <Teleport to="body" :disabled="docked">
    <div :class="docked ? 'chat-dock' : 'sheet-backdrop'" @click.self="docked || $emit('close')">
      <div :class="docked ? 'chat chat--docked' : 'sheet chat chat--sheet'" :role="docked ? null : 'dialog'" aria-label="Chat">
        <button v-if="!docked" class="sheet__close" aria-label="Close" @click="$emit('close')">✕</button>
        <h2 v-if="!docked" class="chat__title">Chat</h2>
        <ol ref="list" class="chat__list" aria-live="polite">
          <li v-if="!messages.length" class="chat__empty muted">
            {{ chat ? 'Nothing yet. Say hello, or ask for help with a clue.' : 'Loading…' }}
          </li>
          <li v-for="m in messages" :key="m.id" class="chat__msg" :class="{ 'is-mine': m.uid === myUid }" :title="when(m.at)">
            <span class="chat__name" :style="{ color: m.color }">{{ m.uid === myUid ? 'You' : m.name }}</span>
            <span class="chat__text">{{ m.text }}</span>
          </li>
        </ol>
        <form class="chat__form" @submit.prevent="send">
          <input
            ref="input"
            v-model="draft"
            class="chat__input"
            :maxlength="max"
            placeholder="Message everyone solving"
            autocomplete="off"
            enterkeyhint="send"
            aria-label="Message"
            @keydown.esc="docked || $emit('close')"
          >
          <button type="submit" class="primary chat__send" :disabled="!draft.trim() || sending">Send</button>
        </form>
        <p v-if="failed" class="notice notice--bad chat__failed">That didn’t send. Check your connection and try again.</p>
      </div>
    </div>
  </Teleport>
</template>

<script>
// The room's chat (Matt, 2026-10-22: "It would be nice to have a little chat
// room on the group solves"). Each name in that solver's colour. Typing here
// never reaches the grid: PlayScreen's key handler skips a focused input.
import { mapState, mapGetters } from 'vuex';
import { CHAT_MAX } from '@/utils/chat';

export default {
  name: 'ChatPanel',
  props: {
    docked: { type: Boolean, default: false },
  },
  emits: ['close'],
  data() {
    return {
      draft: '', sending: false, failed: false, max: CHAT_MAX,
    };
  },
  computed: {
    ...mapState(['chat']),
    ...mapGetters(['myUid']),
    messages() { return this.chat || []; },
  },
  watch: {
    messages() { this.seen(); },
  },
  mounted() {
    this.seen();
    // On a phone the sheet is opened to talk; beside the grid the tab is
    // opened to read, and taking focus there would only be in the way.
    if (!this.docked) this.$nextTick(() => this.$refs.input?.focus());
  },
  methods: {
    seen() {
      this.$store.dispatch('markChatRead');
      this.$nextTick(() => {
        const list = this.$refs.list;
        if (list) list.scrollTop = list.scrollHeight;
      });
    },
    async send() {
      const text = this.draft;
      if (!text.trim() || this.sending) return;
      this.sending = true;
      this.failed = false;
      const ok = await this.$store.dispatch('sendChat', text);
      this.sending = false;
      if (ok) this.draft = '';
      else this.failed = true;
      this.$refs.input?.focus();
    },
    when(at) {
      return at ? new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.chat { display: flex; flex-direction: column; gap: 0.5rem; }
.chat--sheet {
  height: min(70dvh, 560px);
  // Clear of the bug button, which sits over every sheet's bottom-left corner.
  padding-bottom: calc(3.6rem + var(--safe-bottom));
}
.chat__title { margin: 0; font-size: 1.05rem; }
.chat-dock { min-height: 0; display: flex; flex: 1; }
.chat--docked {
  flex: 1; min-height: 0;
  padding: 0.9rem 1rem; border: 1px solid var(--edge); border-radius: var(--radius);
  background: rgba(0, 0, 0, 0.15);
}
.chat__list {
  flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain;
  list-style: none; margin: 0; padding: 0 0.1rem;
  display: flex; flex-direction: column; gap: 0.4rem;
}
.chat__empty { margin: auto 0; text-align: center; font-size: 0.9rem; }
.chat__msg {
  align-self: flex-start; max-width: 85%;
  background: var(--surface-raised); border: 1px solid var(--edge); border-radius: 12px;
  padding: 0.35rem 0.65rem; line-height: 1.3;
  &.is-mine { align-self: flex-end; background: rgba(255, 255, 255, 0.06); }
}
.chat__name { display: block; font-size: 0.72rem; font-weight: 700; }
.chat__text { font-size: 0.95rem; white-space: pre-wrap; overflow-wrap: anywhere; }
.chat__form { display: flex; gap: 0.4rem; }
// 16px or iOS zooms the page in on focus.
.chat__input { flex: 1; min-width: 0; font-size: 16px; }
.chat__send { padding: 0 1rem; }
.chat__failed { margin: 0; font-size: 0.85rem; }
</style>
